import { Dispatch, SetStateAction, useState } from "react";
import { Formik, Field, ErrorMessage } from "formik";
import { object, string } from "yup";
import { formInputTheme } from "hardcover-ui/theme/components/form/input";
import Button from "hardcover-ui/components/Button";
import Heading from "hardcover-ui/components/Heading";
import Label from "hardcover-ui/components/Label";
import Text from "hardcover-ui/components/Text";
import { toast } from "sonner";
import TextLink from "hardcover-ui/components/TextLink";
import { router } from "@inertiajs/react";

interface Props {
  onComplete: Dispatch<SetStateAction<boolean>>;
  complete: boolean;
}

const LoginForm = ({ complete, onComplete: setComplete }: Props) => {
  const [error, setError] = useState(null);
  
  if (complete) {
    return (
      <div>
        <Heading variant="primary" size="md" className="">
          Welcome Back!
        </Heading>
        <Text variant="secondary">Loading your library...</Text>
      </div>
    );
  }

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
      const response = await fetch("/users/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          user: {
            email: values.email,
            password: values.password,
            remember_me: values.remember_me
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setComplete(true);
        setError(null);
        if(data.redirect_to) {
          toast.success("Login successful! Redirecting...");
          window.location = data.redirect_to;
        } else {
          toast.success("Login successful! Loading your library...");
          router.reload();
        }
      } else {
        setError(data.error || data.message || "Login failed");
        toast.error(data.error || data.message || "Login failed");
      }
    } catch {
      console.error('Login error:', error);
      setError("An error occurred during login");
      toast.error("An error occurred during login");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Heading variant="primary" size="md" className="">
        Log in to Hardcover
      </Heading>
      <Formik
        initialValues={{ email: "", password: "" }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={object({
          email: string()
            .max(500, "Must be 500 characters or less")
            .email("Invalid email address")
            .required("Please enter your email"),
          password: string()
            .min(6, "Must be at least 6 characters")
            .max(128, "Must be 128 characters or less")
            .required("Please enter your password"),
        })}
        onSubmit={handleLogin}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className="space-y-4 mt-4">
            {error ? (
              <>
              {error === "You have to confirm your email address before continuing." ? (
                <Text variant="accent" className="py-2">
                  In order to login, you'll need to
                  <TextLink variant="accent" href="/users/confirmation/new" className="ml-1">
                    confirm your email address
                  </TextLink>. Don't worry, it's just once and you're in!
                </Text>
              ) : (
                <Text variant="warning" className="py-2">
                  {error}
                </Text>
              )}
              </>
            ) : (
              false
            )}
            <div>
              <Label size="lg">
                Email
                <Field
                  name="email"
                  aria-label="enter your email"
                  aria-required="true"
                  type="email"
                  autoFocus
                  className={formInputTheme.variant.normal}
                />
              </Label>
              <Text variant="warning">
                <ErrorMessage name="email" />
              </Text>
            </div>
            <div>
              <Label size="lg">
                Password
                <Field
                  name="password"
                  aria-label="enter your password"
                  aria-required="true"
                  type="password"
                  className={formInputTheme.variant.normal}
                />
              </Label>
              <Text variant="warning">
                <ErrorMessage name="password" />
              </Text>
            </div>

            <div>
              <Label size="md" className="flex items-center">
                <Field
                  name="remember_me"
                  aria-label="Remember me for 6 months"
                  aria-required="true"
                  type="checkbox"
                  default={true}
                  className="form-checkbox focus:ring-indigo-500 h-8 w-8 text-indigo-600 border-gray-300 rounded cursor-pointer"
                />
                <span className="ml-3">
                  Remember me?
                </span>
              </Label>
              <Text variant="warning">
                <ErrorMessage name="remember_me" />
              </Text>
            </div>

            <Button
              variant="primary"
              size="lg"
              type="submit"
              className="block w-full justify-center"
              isLoading={formik.isSubmitting}
            >
              Sign In
            </Button>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default LoginForm;
